import {useMutation} from '@github-ui/react-query'
import {useReportErrorContext} from '@github-ui/react-core/future/report-error-context'
import {reactFetchJSON} from '@github-ui/verified-fetch'
import {useCallback} from 'react'

interface UseWorkflowPinningArgs {
  owner: string
  repo: string
  refreshWorkflows: () => void
}

type WorkflowId = number | bigint
type WorkflowPinningAction = {type: 'pin' | 'unpin'; workflowId: WorkflowId}

export function useWorkflowPinning({owner, repo, refreshWorkflows}: UseWorkflowPinningArgs) {
  const reportError = useReportErrorContext()
  const {mutate} = useMutation({
    mutationKey: ['actions-workflows', 'workflow-pinning', owner, repo],
    mutationFn: async ({type, workflowId}: WorkflowPinningAction) => {
      const encodedWorkflowId = encodeURIComponent(workflowId.toString())
      const response = await reactFetchJSON(
        type === 'pin'
          ? `/${owner}/${repo}/actions/pinned_workflows?workflow_id=${encodedWorkflowId}`
          : `/${owner}/${repo}/actions/pinned_workflows/${encodedWorkflowId}`,
        {
          method: type === 'pin' ? 'POST' : 'DELETE',
        },
      )
      if (!response.ok) throw new Error(`Failed to ${type} workflow (${response.status})`)
    },
    onError: error => reportError(error),
    onSettled: refreshWorkflows,
  })

  const pinWorkflow = useCallback((workflowId: WorkflowId) => mutate({type: 'pin', workflowId}), [mutate])
  const unpinWorkflow = useCallback((workflowId: WorkflowId) => mutate({type: 'unpin', workflowId}), [mutate])

  return {pinWorkflow, unpinWorkflow}
}
